import React, { useEffect, useState } from "react";
import { Loader2, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { dataTrigger } from "@/analytics";
import { confirmEmailCode, resendVerification } from "@/api/auth";
import { useAuth } from "@/store/authStore";
import { Button } from "@/ui/Button/Button";
import cls from "./styles.module.scss";

const RESEND_SECONDS = 60;

function serverError(e: unknown, fallback: string): string {
  const data = (e as { response?: { data?: { error?: string } } })?.response?.data;
  return data?.error || fallback;
}

interface EmailCodeStepProps {
  email: string;
  /** confirm — после регистрации; login — «войти по коду из письма». */
  purpose: "confirm" | "login";
  onChangeEmail: () => void;
}

/**
 * «Проверьте почту»: 6 цифр из письма — и сразу вход. Ссылка из письма тоже работает.
 * Почта уже подтверждена (повторная регистрация, забытый пароль) — в письме код для входа.
 */
export function EmailCodeStep({ email, purpose, onChangeEmail }: EmailCodeStepProps) {
  const navigate = useNavigate();
  const setTokens = useAuth((s) => s.setTokens);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [wait, setWait] = useState(RESEND_SECONDS);

  useEffect(() => {
    if (wait <= 0) return;
    const t = window.setTimeout(() => setWait((w) => w - 1), 1000);
    return () => window.clearTimeout(t);
  }, [wait]);

  const digits = code.replace(/\D/g, "");

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (digits.length !== 6) return setError("Код — 6 цифр из письма");
    setError("");
    setStatus("");
    try {
      setLoading(true);
      const tokens = await confirmEmailCode(email, digits);
      setTokens(tokens.access, tokens.refresh, "email");
      navigate("/");
    } catch (err) {
      setError(serverError(err, "Не получилось проверить код. Попробуйте ещё раз."));
    } finally {
      setLoading(false);
    }
  };

  const onResend = async () => {
    setError("");
    setStatus("");
    try {
      setLoading(true);
      await resendVerification(email);
      setStatus("Отправили новое письмо. Код из прошлого письма больше не подходит.");
      setCode("");
      setWait(RESEND_SECONDS);
    } catch (err) {
      setError(serverError(err, "Не удалось отправить письмо. Попробуйте позже."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className={cls.form}>
      <div className={cls.codeHead}>
        <Mail className={cls.codeIcon} aria-hidden />
        <p className={cls.tgTitle}>Проверьте почту</p>
        <p className={cls.codeText}>
          {purpose === "confirm" ? "Мы отправили письмо на " : "Если аккаунт с почтой "}
          <b>{email}</b>
          {purpose === "confirm"
            ? ". Введите код из письма или откройте ссылку в нём."
            : " есть, мы отправили на неё код для входа."}
        </p>
      </div>

      <div className={cls.field}>
        <label className={cls.label}>Код из письма</label>
        <input
          className={`${cls.input} ${cls.codeInput}`}
          value={code}
          onChange={(e) => {
            setCode(e.target.value.replace(/[^\d ]/g, "").slice(0, 7));
            setError("");
          }}
          placeholder="000000"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
        />
      </div>

      {error ? <div className={cls.error}>{error}</div> : null}
      {status ? <div className={cls.status}>{status}</div> : null}

      <Button
        variant="primary"
        size="lg"
        type="submit"
        disabled={loading || digits.length !== 6}
        aria-busy={loading}
        leftIcon={loading ? <Loader2 className={cls.loaderIcon} /> : undefined}
        {...dataTrigger("bkn.auth.code_submit")}
      >
        Войти
      </Button>

      <Button
        variant="secondary"
        size="md"
        type="button"
        onClick={onResend}
        disabled={loading || wait > 0}
        {...dataTrigger("bkn.auth.code_resend")}
      >
        {wait > 0 ? `Отправить ещё раз через ${wait} с` : "Отправить ещё раз"}
      </Button>

      <p className={cls.codeHint}>Письма нет? Проверьте папку «Спам». Код действует 30 минут.</p>

      <div className={cls.switchRow}>
        <button type="button" className={cls.linkBtn} onClick={onChangeEmail} {...dataTrigger("bkn.auth.code_change_email")}>
          Изменить почту
        </button>
      </div>
    </form>
  );
}
